<!-- @format -->

<template>
	<AccordionPanel :value="value">
		<AccordionHeader>
			<div class="dnb-principal-item__header">
				<i
					v-if="principal.isSigningAuthority === true"
					v-tooltip="{ value: $t('§eq_operator.dnb_signing_authority_confirmed') }"
					class="pi pi-shield dnb-principal-item__signing-icon"
				/>
				<span class="dnb-principal-item__name">{{ displayName }}</span>
				<span
					v-if="jobTitle"
					class="dnb-principal-item__job-title"
				>
					· {{ jobTitle }}
				</span>
				<span class="dnb-principal-item__badges">
					<Badge
						:value="$t(confidenceLabelKey)"
						:severity="confidenceSeverity"
					/>
					<Tag
						:value="$t(statusLabelKey)"
						:severity="statusSeverity"
					/>
				</span>
			</div>
		</AccordionHeader>

		<AccordionContent>
			<Message
				v-if="state.saveError"
				severity="error"
				class="mb-3"
			>
				{{ saveErrorText }}
			</Message>

			<div
				v-if="state.status === 'dismissed'"
				class="dnb-principal-item__dismissed"
			>
				<p>{{ $t('§eq_operator.dnb_item_dismissed_note') }}</p>
				<CoreButton
					variant="secondary"
					icon="pi-undo"
					label="§ui_button.undismiss"
					@click="$emit('undismiss')"
				/>
			</div>

			<template v-else>
				<div
					v-if="state.status === 'resolved' || state.status === 'saved'"
					class="dnb-principal-item__resolved"
				>
					<h4 class="dnb-principal-item__section-heading">
						{{ $t(resolutionHeadingKey) }}
					</h4>

					<div class="dnb-principal-item__confirmed-card">
						<div class="dnb-principal-item__confirmed-card-avatar">
							<CoreIcon
								name="account-check-outline"
								:size="28"
								fillColor="var(--color-primary-700)"
							/>
						</div>
						<div class="dnb-principal-item__confirmed-card-main">
							<p class="dnb-principal-item__confirmed-card-name">
								{{ state.stagedData.first_name }} {{ state.stagedData.last_name }}
							</p>
							<div class="dnb-principal-item__confirmed-card-meta">
								<div class="dnb-principal-item__confirmed-card-meta-item">
									<span class="dnb-principal-item__confirmed-card-meta-label">
										{{ $t('§eq_operator.auth_person_date_of_birth') }}
									</span>
									<span class="dnb-principal-item__confirmed-card-meta-value">
										{{ stagedDisplayValue('date_of_birth') || '—' }}
									</span>
								</div>
								<div class="dnb-principal-item__confirmed-card-meta-item">
									<span class="dnb-principal-item__confirmed-card-meta-label">
										{{ $t('§eq_operator.auth_person_country') }}
									</span>
									<span class="dnb-principal-item__confirmed-card-meta-value">
										{{ stagedDisplayValue('country_id') || '—' }}
									</span>
								</div>
								<div
									v-if="jobTitle"
									class="dnb-principal-item__confirmed-card-meta-item"
								>
									<span class="dnb-principal-item__confirmed-card-meta-label">
										{{ $t('§eq_operator.dnb_principal_card_job_title') }}
									</span>
									<span class="dnb-principal-item__confirmed-card-meta-value">
										{{ jobTitle }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div
					v-else-if="showPreviewCard"
					class="mb-3"
				>
					<div
						v-if="conflictingFields.length > 0"
						class="dnb-principal-item__conflicts"
					>
						<h4 class="dnb-principal-item__section-heading">
							{{ $t('§eq_operator.dnb_conflicts_heading') }}
						</h4>
						<DnbFieldConflictResolver
							v-for="field in conflictingFields"
							:key="field"
							:field="field"
							:label="fieldLabel(field)"
							:savedValue="savedValueOf(field)"
							:dnbValue="dnbValueOf(field)"
							:modelValue="state.conflictChoices[field] || 'saved'"
							:countryLabelByCountryId="countryLabelByCountryId"
							@update:modelValue="handleConflictChoice(field, $event)"
						/>
					</div>

					<div class="dnb-principal-item__card-pair">
						<DnbPrincipalCard
							class="dnb-principal-item__card-pair-card"
							:principal="principal"
							:countryLabel="dnbCountryLabel"
							:matchingFields="selectedMatchingFields"
						/>

						<div class="dnb-principal-item__card-pair-icon">
							<CoreIcon
								name="format-horizontal-align-left"
								:size="28"
								fillColor="var(--color-neutral-900)"
							/>
						</div>

						<DnbMatchCard
							class="dnb-principal-item__card-pair-card"
							:person="selectedPerson"
							:score="selectedMatch.score"
							:tier="selectedMatch.tier"
							:selected="true"
							:countryLabel="countryLabelByCountryId[selectedPerson.country_id] || ''"
							:matchingFields="selectedMatchingFields"
						/>
					</div>
				</div>

				<div
					v-else-if="isDisambiguating"
					class="mb-3 dnb-principal-item__disambiguate"
				>
					<NameDisambiguator
						:key="principal.fullName"
						:fullName="principal.fullName || ''"
						:initial="state.nameAssignment || null"
						@confirm="handleDisambiguatorConfirm"
						@cancel="handleDisambiguatorCancel"
						@update:assignment="handleDisambiguatorAssignmentUpdate"
					>
						<template #footer-extra>
							<CoreIconButton
								tooltip="§ui_button.dismiss"
								color="error-light"
								icon="close"
								iconSize="24"
								@buttonClick="$emit('dismiss')"
							/>
						</template>
					</NameDisambiguator>
				</div>

				<div
					v-else
					class="mb-3 dnb-principal-item__columns"
				>
					<div
						:class="[
							'dnb-principal-item__left-col',
							{ 'dnb-principal-item__left-col--full': isResolvingNew },
						]"
					>
						<h4 class="dnb-principal-item__section-heading">
							{{ $t('§eq_operator.dnb_data_heading') }}
						</h4>

						<DnbPrincipalCard
							v-if="isPending"
							:principal="principal"
							:countryLabel="dnbCountryLabel"
							:matchingFields="anyMatchingFields"
						/>

						<template v-else>
							<div
								v-if="conflictingFields.length > 0"
								class="dnb-principal-item__conflicts"
							>
								<h4 class="dnb-principal-item__section-heading">
									{{ $t('§eq_operator.dnb_conflicts_heading') }}
								</h4>
								<DnbFieldConflictResolver
									v-for="field in conflictingFields"
									:key="field"
									:field="field"
									:label="fieldLabel(field)"
									:savedValue="savedValueOf(field)"
									:dnbValue="dnbValueOf(field)"
									:modelValue="state.conflictChoices[field] || 'saved'"
									:countryLabelByCountryId="countryLabelByCountryId"
									@update:modelValue="handleConflictChoice(field, $event)"
								/>
							</div>

							<AuthorizedPersonFormFields
								:modelValue="state.stagedData"
								:validator="v$"
								:disabledFields="disabledFormFields"
								@update:modelValue="handleFormUpdate"
							/>
						</template>
					</div>

					<div
						v-if="matches.length > 0 && !isResolvingNew"
						class="dnb-principal-item__columns-icon"
					>
						<CoreIcon
							name="format-horizontal-align-left"
							:size="28"
							fillColor="var(--color-neutral-900)"
						/>
					</div>

					<div
						v-if="!isResolvingNew"
						class="dnb-principal-item__right-col"
					>
						<h4 class="dnb-principal-item__section-heading">
							{{ $t('§eq_operator.dnb_matches_heading') }}
						</h4>

						<div
							v-if="matches.length === 0"
							class="dnb-principal-item__empty"
						>
							{{ $t('§eq_operator.dnb_no_match_found') }}
						</div>

						<div
							v-else
							class="dnb-principal-item__match-list"
						>
							<DnbMatchCard
								v-for="m in matches"
								:key="m.person.id"
								:person="m.person"
								:score="m.score"
								:tier="m.tier"
								:selected="state.selectedPersonId === m.person.id"
								:countryLabel="countryLabelByCountryId[m.person.country_id] || ''"
								:matchingFields="getMatchingFields(m.person)"
								@select="handleSelect(m.person.id)"
							/>
						</div>
					</div>
				</div>

				<div
					v-if="state.status !== 'saved' && !isDisambiguating"
					class="dnb-principal-item__footer"
				>
					<div class="dnb-principal-item__footer-primary">
						<CoreButton
							v-if="state.status === 'resolved'"
							icon="pi-pencil"
							label="§ui_button.edit"
							variant="secondary"
							@click="$emit('re-open')"
						/>
						<CoreButton
							v-else-if="isResolvingMatch"
							icon="pi-check"
							label="§ui_button.done"
							:disabled="!isFormValid"
							@click="handleConfirmMatchDone"
						/>
						<CoreButton
							v-else-if="isResolvingNew"
							icon="pi-check"
							label="§ui_button.done"
							:disabled="!isFormValid"
							@click="handleSaveAsNewDone"
						/>
						<CoreButton
							v-else
							icon="pi-plus"
							label="§ui_button.save_as_new_person"
							:variant="state.selectedPersonId === null ? 'primary' : 'secondary'"
							@click="handleSaveAsNewClick"
						/>
					</div>

					<div class="dnb-principal-item__footer-secondary">
						<CoreButton
							v-if="isPending && state.selectedPersonId !== null"
							icon="pi-link"
							label="§ui_button.confirm_match"
							@click="handleConfirmMatchClick"
						/>
					</div>

					<div class="dnb-principal-item__footer-right">
						<CoreIconButton
							v-if="state.status === 'resolved'"
							tooltip="§ui_button.undo_match"
							color="secondary"
							icon="undo"
							iconSize="24"
							@buttonClick="$emit('undo-match')"
						/>
						<CoreIconButton
							tooltip="§ui_button.dismiss"
							color="error-light"
							icon="close"
							iconSize="24"
							@buttonClick="$emit('dismiss')"
						/>
					</div>
				</div>
			</template>
		</AccordionContent>
	</AccordionPanel>
</template>

<script>
import { parseISO } from 'date-fns'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength } from '@vuelidate/validators'
import AccordionPanel from 'primevue/accordionpanel'
import AccordionHeader from 'primevue/accordionheader'
import AccordionContent from 'primevue/accordioncontent'
import Badge from 'primevue/badge'
import Tag from 'primevue/tag'
import Message from 'primevue/message'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreIconButton from '@/components/core/CoreIconButton.vue'
import AuthorizedPersonFormFields from './AuthorizedPersonFormFields.vue'
import DnbMatchCard from './DnbMatchCard.vue'
import DnbFieldConflictResolver from './DnbFieldConflictResolver.vue'
import DnbPrincipalCard from './DnbPrincipalCard.vue'
import NameDisambiguator from './NameDisambiguator.vue'
import CoreIcon from '@/components/core/CoreIcon.vue'
import {
	buildCountryIdByIsoMap,
	deriveNameParts,
	needsDisambiguation,
	toIsoDateOnly,
} from '@/helpers/dnbMatching'
import { isSignatoryResidencyCountry, signatoryResidencyCountry } from '@/helpers/signatoryResidencyCountry'

const FIELDS = ['first_name', 'last_name', 'date_of_birth', 'country_id']

const FIELD_LABEL_KEYS = {
	first_name: '§eq_operator.auth_person_first_name',
	last_name: '§eq_operator.auth_person_last_name',
	date_of_birth: '§eq_operator.auth_person_date_of_birth',
	country_id: '§eq_operator.auth_person_country',
}

const CONFIDENCE_LABEL_KEYS = {
	confirmed: '§eq_operator.dnb_confidence_confirmed',
	likely: '§eq_operator.dnb_confidence_likely',
	possible: '§eq_operator.dnb_confidence_possible',
	none: '§eq_operator.dnb_confidence_no_match',
}

const CONFIDENCE_SEVERITIES = {
	confirmed: 'success',
	likely: 'warn',
	possible: 'secondary',
	none: 'secondary',
}

const STATUS_LABEL_KEYS = {
	pending: '§eq_operator.dnb_status_pending',
	'resolving-match': '§eq_operator.dnb_status_pending',
	'resolving-new': '§eq_operator.dnb_status_pending',
	disambiguating: '§eq_operator.dnb_status_pending',
	resolved: '§eq_operator.dnb_status_resolved',
	saved: '§eq_operator.dnb_status_resolved',
	dismissed: '§eq_operator.dnb_status_dismissed',
}

const STATUS_SEVERITIES = {
	pending: 'warn',
	'resolving-match': 'warn',
	'resolving-new': 'warn',
	disambiguating: 'warn',
	resolved: 'success',
	saved: 'success',
	dismissed: 'secondary',
}

const rules = {
	first_name: {
		required,
		minLength: minLength(2),
		maxLength: maxLength(200),
	},
	last_name: {
		required,
		minLength: minLength(2),
		maxLength: maxLength(200),
	},
	date_of_birth: { required },
	country_id: { required, signatoryResidencyCountry },
}

export default {
	name: 'DnbPrincipalAccordionItem',

	components: {
		AccordionPanel,
		AccordionHeader,
		AccordionContent,
		Badge,
		Tag,
		Message,
		CoreButton,
		CoreIconButton,
		AuthorizedPersonFormFields,
		DnbMatchCard,
		DnbFieldConflictResolver,
		DnbPrincipalCard,
		NameDisambiguator,
		CoreIcon,
	},

	props: {
		principal: {
			type: Object,
			required: true,
		},
		value: {
			type: [String, Number],
			required: true,
		},
		matches: {
			type: Array,
			default: () => [],
		},
		state: {
			type: Object,
			required: true,
		},
		countryIsoByCountryId: {
			type: Object,
			default: () => ({}),
		},
		countryLabelByCountryId: {
			type: Object,
			default: () => ({}),
		},
	},

	emits: [
		'update:state',
		'dismiss',
		'undismiss',
		'confirm-match',
		'save-as-new',
		're-open',
		'undo-match',
	],

	data() {
		return {
			v$: {},
		}
	},

	computed: {
		displayName() {
			const parts = [this.principal.givenName, this.principal.familyName]
				.filter(Boolean)
				.join(' ')
				.trim()
			return this.principal.fullName || parts || '—'
		},

		jobTitle() {
			const titles = Array.isArray(this.principal.jobTitles) ? this.principal.jobTitles : []
			return titles[0]?.title || ''
		},

		bestMatch() {
			return this.matches[0] || null
		},

		confidenceTier() {
			if (!this.bestMatch) return 'none'
			return this.bestMatch.tier
		},

		confidenceLabelKey() {
			return CONFIDENCE_LABEL_KEYS[this.confidenceTier]
		},

		confidenceSeverity() {
			return CONFIDENCE_SEVERITIES[this.confidenceTier]
		},

		statusLabelKey() {
			if (this.state.saveError) return '§eq_operator.dnb_status_error'
			return STATUS_LABEL_KEYS[this.state.status] || STATUS_LABEL_KEYS.pending
		},

		statusSeverity() {
			if (this.state.saveError) return 'danger'
			return STATUS_SEVERITIES[this.state.status] || STATUS_SEVERITIES.pending
		},

		isPending() {
			return this.state.status === 'pending'
		},

		dnbCountryLabel() {
			const countryId = this.dnbMappedData.country_id
			if (countryId === null || countryId === undefined) return ''
			return this.countryLabelByCountryId[countryId] || ''
		},

		isResolvingMatch() {
			return this.state.status === 'resolving-match'
		},

		isResolvingNew() {
			return this.state.status === 'resolving-new'
		},

		isDisambiguating() {
			return this.state.status === 'disambiguating'
		},

		hasMissingFields() {
			if (!this.selectedPerson) return true
			return FIELDS.some((field) => !this.isSavedValueUsable(field, this.selectedPerson[field]))
		},

		// The saved person's country is no longer allowed for signatories, so a match must make the user pick one.
		needsCountryPick() {
			return (
				this.isResolvingMatch &&
				!!this.selectedPerson &&
				!this.isSavedValueUsable('country_id', this.selectedPerson.country_id)
			)
		},

		showPreviewCard() {
			return this.isResolvingMatch && !this.hasMissingFields
		},

		selectedMatch() {
			if (this.state.selectedPersonId === null) return null
			return this.matches.find((m) => m.person.id === this.state.selectedPersonId) || null
		},

		selectedPerson() {
			return this.selectedMatch ? this.selectedMatch.person : null
		},

		selectedMatchingFields() {
			return this.selectedPerson ? this.getMatchingFields(this.selectedPerson) : {}
		},

		anyMatchingFields() {
			if (this.matches.length === 0) return {}
			const result = {}
			FIELDS.forEach((field) => {
				result[field] = false
			})
			for (const m of this.matches) {
				const mf = this.getMatchingFields(m.person)
				FIELDS.forEach((field) => {
					if (mf[field]) result[field] = true
				})
			}
			return result
		},

		saveErrorText() {
			const message = this.state.saveError
			if (typeof message === 'string' && message.startsWith('§')) return this.$t(message)
			return message
		},

		dnbStagingData() {
			const countryId = this.dnbMappedData.country_id
			return {
				...this.dnbMappedData,
				country_id: isSignatoryResidencyCountry(countryId) ? countryId : null,
			}
		},

		dnbMappedData() {
			const iso = Array.isArray(this.principal.nationality)
				? this.principal.nationality[0]?.isoAlpha2Code
				: null
			const countryIdByIso = this.countryIdByIsoMap
			const { first_name, last_name } = deriveNameParts(this.principal)
			return {
				first_name,
				last_name,
				date_of_birth: this.principal.birthDate ? parseISO(this.principal.birthDate) : null,
				country_id:
					iso && countryIdByIso[iso.toUpperCase()] !== undefined
						? countryIdByIso[iso.toUpperCase()]
						: null,
			}
		},

		countryIdByIsoMap() {
			return buildCountryIdByIsoMap(this.countryIsoByCountryId)
		},

		conflictingFields() {
			if (!this.isResolvingMatch || !this.selectedPerson) return []
			const saved = this.selectedPerson
			return FIELDS.filter((field) => this.fieldsConflict(field, saved))
		},

		disabledFormFields() {
			if (!this.isResolvingMatch || !this.selectedPerson) return []
			return FIELDS.filter(
				(field) =>
					!this.conflictingFields.includes(field) && this.savedFieldPopulated(field),
			)
		},

		resolutionHeadingKey() {
			return this.state.selectedPersonId !== null
				? '§eq_operator.dnb_resolved_matched_heading'
				: '§eq_operator.dnb_resolved_new_heading'
		},

		isFormValid() {
			if (!this.v$ || typeof this.v$.$invalid !== 'boolean') return false
			return !this.v$.$invalid
		},
	},

	watch: {
		needsCountryPick(needed) {
			if (needed) this.touchCountryAfterRender()
		},
	},

	mounted() {
		this.$nextTick(() => {
			this.v$ = useVuelidate(rules, this.state.stagedData)
			if (this.needsCountryPick) this.touchCountryAfterRender()
		})
	},

	methods: {
		getMatchingFields(person) {
			if (!person) return {}
			const result = {}
			FIELDS.forEach((field) => {
				const dnbVal = this.dnbMappedData[field]
				const personVal = person[field]
				const dnbPopulated = dnbVal !== null && dnbVal !== undefined && dnbVal !== ''
				const personPopulated = personVal !== null && personVal !== undefined && personVal !== ''
				if (!dnbPopulated || !personPopulated) {
					result[field] = false
					return
				}
				if (field === 'date_of_birth') {
					result[field] = toIsoDateOnly(dnbVal) === toIsoDateOnly(personVal)
				} else if (field === 'country_id') {
					result[field] = Number(dnbVal) === Number(personVal)
				} else {
					result[field] =
						String(dnbVal).trim().toLowerCase() === String(personVal).trim().toLowerCase()
				}
			})
			return result
		},

		fieldLabel(field) {
			return this.$t(FIELD_LABEL_KEYS[field])
		},

		// A saved value counts only when populated; a saved country must also still be allowed for signatories.
		isSavedValueUsable(field, value) {
			if (value === null || value === undefined || value === '') return false
			if (field === 'country_id') return isSignatoryResidencyCountry(value)
			return true
		},

		savedFieldPopulated(field) {
			return this.isSavedValueUsable(field, this.selectedPerson?.[field])
		},

		fieldsConflict(field, saved) {
			const savedVal = saved?.[field]
			const dnbVal = this.dnbStagingData[field]
			const savedPopulated = this.isSavedValueUsable(field, savedVal)
			const dnbPopulated = dnbVal !== null && dnbVal !== undefined && dnbVal !== ''
			if (!savedPopulated || !dnbPopulated) return false

			if (field === 'date_of_birth') {
				return toIsoDateOnly(savedVal) !== toIsoDateOnly(dnbVal)
			}
			if (field === 'country_id') {
				return Number(savedVal) !== Number(dnbVal)
			}
			return String(savedVal).trim().toLowerCase() !== String(dnbVal).trim().toLowerCase()
		},

		savedValueOf(field) {
			return this.selectedPerson ? this.selectedPerson[field] : null
		},

		dnbValueOf(field) {
			const val = this.dnbStagingData[field]
			if (field === 'date_of_birth' && val instanceof Date) {
				return toIsoDateOnly(val)
			}
			return val
		},

		stagedDisplayValue(field) {
			const val = this.state.stagedData?.[field]
			if (val === null || val === undefined || val === '') return ''
			if (field === 'date_of_birth') {
				try {
					const date = val instanceof Date ? val : parseISO(String(val))
					return this.displayAsDate(date)
				} catch {
					return ''
				}
			}
			if (field === 'country_id') {
				return this.countryLabelByCountryId[val] || ''
			}
			return String(val)
		},

		emitPatch(patch) {
			this.$emit('update:state', { ...this.state, ...patch })
		},

		handleSelect(personId) {
			const next = this.state.selectedPersonId === personId ? null : personId
			this.emitPatch({ selectedPersonId: next })
		},

		handleFormUpdate(patch) {
			this.emitPatch({
				stagedData: { ...this.state.stagedData, ...patch },
			})
		},

		handleConflictChoice(field, choice) {
			const savedVal = this.savedValueOf(field)
			const dnbVal = this.dnbStagingData[field]
			const nextValue = choice === 'saved' ? this.coerceForStaging(field, savedVal) : dnbVal

			this.emitPatch({
				conflictChoices: { ...this.state.conflictChoices, [field]: choice },
				stagedData: { ...this.state.stagedData, [field]: nextValue },
			})
		},

		coerceForStaging(field, value) {
			if (field === 'date_of_birth') {
				if (!value) return null
				if (value instanceof Date) return value
				try {
					return parseISO(String(value))
				} catch {
					return null
				}
			}
			return value
		},

		handleConfirmMatchClick() {
			if (this.state.selectedPersonId === null || !this.selectedPerson) return

			const saved = this.selectedPerson
			const merged = {}
			const conflictChoices = {}
			FIELDS.forEach((field) => {
				const savedVal = saved[field]
				const dnbVal = this.dnbStagingData[field]
				const savedPopulated = this.isSavedValueUsable(field, savedVal)

				if (this.fieldsConflict(field, saved)) {
					conflictChoices[field] = 'saved'
					merged[field] = this.coerceForStaging(field, savedVal)
				} else if (savedPopulated) {
					merged[field] = this.coerceForStaging(field, savedVal)
				} else {
					merged[field] = dnbVal
				}
			})

			this.emitPatch({
				status: 'resolving-match',
				conflictChoices,
				stagedData: merged,
			})

			if (Object.keys(conflictChoices).length === 0 && !this.hasMissingFields) {
				this.$emit('confirm-match')
			}
		},

		handleSaveAsNewClick() {
			if (needsDisambiguation(this.principal)) {
				this.emitPatch({
					status: 'disambiguating',
					selectedPersonId: null,
					conflictChoices: {},
					stagedData: { ...this.dnbStagingData },
				})
				return
			}
			this.emitPatch({
				status: 'resolving-new',
				selectedPersonId: null,
				conflictChoices: {},
				stagedData: { ...this.dnbStagingData },
			})
		},

		handleDisambiguatorConfirm(payload) {
			const { first_name, last_name, assignment } = payload
			this.emitPatch({
				status: 'resolving-new',
				selectedPersonId: null,
				conflictChoices: {},
				stagedData: {
					...this.state.stagedData,
					first_name,
					last_name,
				},
				nameAssignment: assignment,
			})
		},

		handleDisambiguatorCancel() {
			this.emitPatch({
				status: 'pending',
				stagedData: { ...this.dnbStagingData },
				nameAssignment: null,
			})
		},

		handleDisambiguatorAssignmentUpdate(assignment) {
			this.emitPatch({ nameAssignment: assignment })
		},

		// Show the country error once the form has rendered: CoreDropdown resets an empty field's validator on mount.
		touchCountryAfterRender() {
			this.$nextTick(() => {
				if (this.needsCountryPick && this.v$?.country_id?.$touch) this.v$.country_id.$touch()
			})
		},

		handleConfirmMatchDone() {
			if (this.v$.$touch) this.v$.$touch()
			if (!this.isFormValid) return
			this.$emit('confirm-match')
		},

		handleSaveAsNewDone() {
			if (this.v$.$touch) this.v$.$touch()
			if (!this.isFormValid) return
			this.$emit('save-as-new')
		},
	},
}
</script>

<style>
.dnb-principal-item__header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	width: 100%;
	padding: 0.25rem 0;
}

.dnb-principal-item__signing-icon {
	color: var(--color-primary);
	font-size: 2.2rem;
}

.dnb-principal-item__name {
	font-weight: 600;
	font-size: 1.5rem;
	color: var(--text-dark);
}

.dnb-principal-item__job-title {
	color: var(--color-neutral-900);
	font-size: 1.4rem;
}

.dnb-principal-item__badges {
	margin-left: auto;
	display: flex;
	gap: 0.75rem;
	align-items: center;
}

.dnb-principal-item__badges .p-badge,
.dnb-principal-item__badges .p-tag {
	font-size: 1.2rem;
	font-weight: 600;
	padding: 0.35rem 0.85rem;
	border-radius: 999px;
	line-height: 1;
	min-height: 2.4rem;
	display: inline-flex;
	align-items: center;
}

.dnb-principal-item__badges .p-badge-success,
.dnb-principal-item__badges .p-tag-success {
	color: var(--color-primary-900);
}

.dnb-principal-item__badges .p-badge-warn,
.dnb-principal-item__badges .p-tag-warn {
	color: var(--color-yellow-900);
}

.dnb-principal-item__badges .p-badge-secondary,
.dnb-principal-item__badges .p-tag-secondary {
	color: var(--color-neutral-900);
}

.dnb-principal-item__badges .p-badge-danger,
.dnb-principal-item__badges .p-tag-danger {
	color: var(--color-red-900);
}

.dnb-principal-item .p-accordioncontent-content {
	padding: 1.25rem 1rem 1rem;
}

.dnb-principal-item__section-heading {
	margin: 0 0 1rem 0;
	font-size: 1.3rem;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	color: var(--color-neutral-900);
}

.dnb-principal-item__columns {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
}

.dnb-principal-item__left-col,
.dnb-principal-item__right-col {
	width: 40%;
	flex: 0 0 40%;
	padding: 0 0.25rem;
}

.dnb-principal-item__left-col--full {
	width: 100%;
	flex: 1 1 auto;
}

.dnb-principal-item__columns-icon {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	padding-left: 3px;
	padding-top: 3px;
	border-radius: 8px;
	background-color: var(--color-neutral-300);
	align-self: flex-start;
	margin-top: 3.7rem;
}

.dnb-principal-item__resolved {
	padding: 0 0.25rem;
}

.dnb-principal-item__resolved .dnb-principal-item__section-heading {
	margin-bottom: 0.5rem;
}

.dnb-principal-item__conflicts {
	margin-bottom: 1.5rem;
}

.dnb-principal-item__match-list {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.dnb-principal-item__empty {
	padding: 2rem 1.5rem;
	text-align: center;
	color: var(--color-neutral-900);
	border: 1px dashed var(--color-neutral-400);
	border-radius: 6px;
	background-color: var(--color-neutral-100);
}

.dnb-principal-item__footer {
	display: flex;
	align-items: center;
	gap: 0;
	margin-top: 1.5rem;
	padding-top: 1rem;
	border-top: 1px solid var(--color-neutral-200, var(--color-neutral-400));
}

.dnb-principal-item__footer-primary {
	flex: 0 0 40%;
	display: flex;
	gap: 0.75rem;
	align-items: center;
}

.dnb-principal-item__footer-secondary {
	flex: 1 1 auto;
	display: flex;
	gap: 0.75rem;
	align-items: center;
	padding-left: 20%;
}

.dnb-principal-item__footer-right {
	flex: 0 0 auto;
	display: flex;
	gap: 0.5rem;
	align-items: center;
	padding-left: 1rem;
}

.dnb-principal-item__card-pair {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.dnb-principal-item__card-pair-card {
	width: 40%;
	flex: 0 0 40%;
}

.dnb-principal-item__card-pair-icon {
	flex: 0 0 auto;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	padding-left: 3px;
	padding-top: 3px;
	border-radius: 8px;
	background-color: var(--color-neutral-300);
}

.dnb-principal-item__confirmed-card {
	display: inline-flex;
	align-items: flex-start;
	gap: 1rem;
	padding: 1rem 1.25rem;
	border: 1px solid var(--color-primary);
	border-radius: 8px;
	background-color: var(--color-primary-300);
}

.dnb-principal-item__confirmed-card-avatar {
	flex: 0 0 auto;
	width: 48px;
	height: 48px;
	border-radius: 50%;
	background-color: var(--color-primary-200);
	display: flex;
	align-items: center;
	justify-content: center;
}

.dnb-principal-item__confirmed-card-main {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
}

.dnb-principal-item__confirmed-card-name {
	font-weight: 600;
	font-size: 1.6rem;
	color: var(--text-dark);
	margin: 0;
}

.dnb-principal-item__confirmed-card-meta {
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
}

.dnb-principal-item__confirmed-card-meta-item {
	display: flex;
	flex-direction: column;
}

.dnb-principal-item__confirmed-card-meta-label {
	color: var(--color-neutral-900);
	font-size: 1.2rem;
}

.dnb-principal-item__confirmed-card-meta-value {
	color: var(--text-dark);
	font-size: 1.4rem;
	font-weight: 500;
}

.dnb-principal-item__dismissed {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 1rem 0;
	color: var(--color-neutral-900);
}
</style>
